import { useEffect, useMemo, useState } from "react";
import { apiRequest } from "../api";

const STORAGE_KEY = "invoice-app-configuration-draft";

const DEFAULT_FORM = {
    invoiceModule: "Both",
    emailNotifications: "N",
    vendorSPOCEmail: "",
    materialApprovalStage: "before_grn",
    serviceApprovalStage: "before_grn",
    typeOfInvoiceEnabled: "Y",
    typeAssignmentMode: "manual",
    automaticTypeChoice: "",
    checklistEnabled: "Y",
};

const SECTION_TITLES = {
    basic: "A. Basic Configurations",
    invoiceType: "B. Type of Invoice Setup",
};

const DEFAULT_OPEN_SECTIONS = {
    basic: true,
    invoiceType: false,
};

function Section({ title, subtitle, open, onToggle, children }) {
    return (
        <article className="config-card">
            <button className="config-card__head" type="button" onClick={onToggle}>
                <div>
                    <div className="config-card__title">{title}</div>
                    <div className="config-card__subtitle">{subtitle}</div>
                </div>
                <span className="config-card__toggle">{open ? "Hide" : "Show"}</span>
            </button>
            {open ? <div className="config-card__body">{children}</div> : null}
        </article>
    );
}

function ChoiceGroup({ name, value, options, onChange }) {
    return (
        <div className="config-inline">
            {options.map((option) => (
                <label key={option.value} className="config-choice">
                    <input
                        type="radio"
                        name={name}
                        value={option.value}
                        checked={value === option.value}
                        onChange={() => onChange(option.value)}
                    />
                    <span>{option.label}</span>
                </label>
            ))}
        </div>
    );
}

function ToggleChoice({ label, value, onChange }) {
    return (
        <label className="config-field">
            <span>{label}</span>
            <select value={value} onChange={(event) => onChange(event.target.value)}>
                <option value="Y">Yes</option>
                <option value="N">No</option>
            </select>
        </label>
    );
}

function normalizeApprovalStage(stage) {
    return String(stage || "")
        .toLowerCase()
        .replace(/[^a-z]/g, "");
}

function isBeforeGrn(stage) {
    return normalizeApprovalStage(stage).includes("beforegrn");
}

function isAfterGrn(stage) {
    return normalizeApprovalStage(stage).includes("aftergrn");
}

function saveDraft(form) {
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify(form));
}

export default function InvoiceAppConfiguration({ onBack }) {
    const [form, setForm] = useState(DEFAULT_FORM);
    const [openSections, setOpenSections] = useState(DEFAULT_OPEN_SECTIONS);
    const [status, setStatus] = useState("");
    const [error, setError] = useState("");
    const [saving, setSaving] = useState(false);
    const [loading, setLoading] = useState(true);

    const selectedModule = useMemo(() => form.invoiceModule, [form.invoiceModule]);
    const requiresOpenPoReport = useMemo(() => {
        const materialNeedsPo = selectedModule !== "Service" && isBeforeGrn(form.materialApprovalStage);
        const serviceNeedsPo = selectedModule !== "Material" && isBeforeGrn(form.serviceApprovalStage);
        return materialNeedsPo || serviceNeedsPo;
    }, [form.materialApprovalStage, form.serviceApprovalStage, selectedModule]);

    useEffect(() => {
        let cancelled = false;

        async function loadConfiguration() {
            setLoading(true);

            try {
                const response = await apiRequest("/invoice-configuration/", {
                    method: "GET",
                });

                if (!cancelled && response.status === "success" && response.data) {
                    const loaded = response.data;
                    setForm((current) => ({
                        ...DEFAULT_FORM,
                        ...current,
                        invoiceModule: loaded.invoiceModule || DEFAULT_FORM.invoiceModule,
                        emailNotifications: loaded.emailNotifications || DEFAULT_FORM.emailNotifications,
                        vendorSPOCEmail: loaded.vendorSPOCEmail || "",
                        materialApprovalStage: loaded.materialApprovalStage || DEFAULT_FORM.materialApprovalStage,
                        serviceApprovalStage: loaded.serviceApprovalStage || DEFAULT_FORM.serviceApprovalStage,
                        typeOfInvoiceEnabled: loaded.typeOfInvoiceEnabled || DEFAULT_FORM.typeOfInvoiceEnabled,
                        typeAssignmentMode: loaded.typeAssignmentMode || DEFAULT_FORM.typeAssignmentMode,
                        automaticTypeChoice: loaded.automaticTypeChoice || "",
                        checklistEnabled: loaded.checklistEnabled || DEFAULT_FORM.checklistEnabled,
                    }));
                    window.localStorage.removeItem(STORAGE_KEY);
                }
            } catch (requestError) {
                if (!cancelled) {
                    setError(requestError.message || "Unable to load saved invoice approval configuration.");
                }
            } finally {
                if (!cancelled) {
                    setLoading(false);
                }
            }
        }

        loadConfiguration();

        return () => {
            cancelled = true;
        };
    }, []);

    function updateField(field, value) {
        setForm((current) => {
            const nextForm = {
                ...current,
                [field]: value,
            };
            saveDraft(nextForm);
            return nextForm;
        });
    }

    function toggleSection(sectionKey) {
        setOpenSections((current) => ({
            ...current,
            [sectionKey]: !current[sectionKey],
        }));
    }

    //-------------------------------------------------------------

    function buildInvoiceConfigurationPayload() {
        const isMaterialActive = form.invoiceModule === "Material" || form.invoiceModule === "Both";
        const isServiceActive = form.invoiceModule === "Service" || form.invoiceModule === "Both";
        const isAutomaticType = form.typeOfInvoiceEnabled === "Y" && form.typeAssignmentMode === "automatic";
        const isInvoiceTypeEnabled = form.typeOfInvoiceEnabled === "Y";

        return {
            invoiceModule: form.invoiceModule,
            emailNotifications: form.emailNotifications,
            vendorSPOCEmail: form.emailNotifications === "Y" ? form.vendorSPOCEmail : "",
            materialApprovalStage: isMaterialActive ? form.materialApprovalStage : "",
            serviceApprovalStage: isServiceActive ? form.serviceApprovalStage : "",
            typeOfInvoiceEnabled: form.typeOfInvoiceEnabled,
            typeAssignmentMode: isInvoiceTypeEnabled ? form.typeAssignmentMode : "",
            automaticTypeChoice: isAutomaticType ? form.automaticTypeChoice : "",
            checklistEnabled: form.checklistEnabled,
        };
    }

    async function handleSaveDraft() {
        setError("");
        saveDraft(form);
        setStatus("Draft saved locally. Sending invoice configuration to backend...");
        setSaving(true);

        try {
            const response = await apiRequest("/invoice-configuration/save/", {
                method: "POST",
                body: buildInvoiceConfigurationPayload(),
            });
            setStatus(response.message || "Draft saved locally and invoice configuration sent to backend.");
            window.localStorage.removeItem(STORAGE_KEY);
        } catch (requestError) {
            setStatus("Draft saved locally in the browser.");
            setError(requestError.message || "Unable to send invoice configuration to backend.");
        } finally {
            setSaving(false);
        }
    }

    //--------------------------------------------------------------

    function handleResetDraft() {
        setError("");
        window.localStorage.removeItem(STORAGE_KEY);
        setForm(DEFAULT_FORM);
        setStatus("Draft reset.");
    }

    const showMaterialStage = selectedModule === "Material" || selectedModule === "Both";
    const showServiceStage = selectedModule === "Service" || selectedModule === "Both";

    if (loading) {
        return <div className="workspace-panel">Loading invoice approval configuration…</div>;
    }

    return (
        <section className="config-view">
            <div className="config-view__header workspace-panel">

                <div>
                    <h2>Invoice App Configuration</h2>
                </div>

                <div className="config-view__actions">
                    <button className="primary-button" type="button" onClick={handleSaveDraft} disabled={saving}>
                        {saving ? "Saving..." : "Save Draft"}
                    </button>
                    <button className="ghost-button" type="button" onClick={handleResetDraft}>
                        Reset Draft
                    </button>
                </div>
                
            </div>

            {status ? <div className="config-banner config-banner--success">{status}</div> : null}
            {error ? <div className="config-banner config-banner--error">{error}</div> : null}

            <Section
                title={SECTION_TITLES.basic}
                subtitle="Choose the invoice scope and initial approval controls"
                open={openSections.basic}
                onToggle={() => toggleSection("basic")}
            >
                <div className="config-grid">
                    <div className="config-field">
                        <label>Invoices to be dealt in this module</label>
                        <ChoiceGroup
                            name="invoiceModule"
                            value={form.invoiceModule}
                            options={[
                                { value: "Material", label: "Material" },
                                { value: "Service", label: "Service" },
                                { value: "Both", label: "Both" },
                            ]}
                            onChange={(value) => updateField("invoiceModule", value)}
                        />
                    </div>

                    <div className="config-field">
                        <label>Activate email notifications to users and vendors?</label>
                        <select
                            value={form.emailNotifications}
                            onChange={(event) => updateField("emailNotifications", event.target.value)}
                        >
                            <option value="N">No</option>
                            <option value="Y">Yes</option>
                        </select>
                    </div>

                    {form.emailNotifications === "Y" ? (
                        <div className="config-field">
                            <label>Vendor SPOC email</label>
                            <input
                                type="email"
                                value={form.vendorSPOCEmail}
                                onChange={(event) => updateField("vendorSPOCEmail", event.target.value)}
                                placeholder="relevant-user@vendor.com"
                            />
                            <div className="config-field__hint">Used for email approval, hold, and reject actions.</div>
                        </div>
                    ) : null}
                </div>

                <div className="config-grid">
                    {showMaterialStage ? (
                        <div className="config-field">
                            <label>Material invoice approval stage</label>
                            <select
                                value={form.materialApprovalStage}
                                onChange={(event) => updateField("materialApprovalStage", event.target.value)}
                            >
                                <option value="before_grn">Before GRN</option>
                                <option value="after_grn">After GRN</option>
                            </select>
                        </div>
                    ) : null}

                    {showServiceStage ? (
                        <div className="config-field">
                            <label>Service invoice approval stage</label>
                            <select
                                value={form.serviceApprovalStage}
                                onChange={(event) => updateField("serviceApprovalStage", event.target.value)}
                            >
                                <option value="before_grn">Before GRN</option>
                                <option value="after_grn">After GRN</option>
                            </select>
                        </div>
                    ) : null}
                </div>
            </Section>

            <Section
                title={SECTION_TITLES.invoiceType}
                subtitle="Enable invoice type setup and choose how types are assigned"
                open={openSections.invoiceType}
                onToggle={() => toggleSection("invoiceType")}
            >
                <div className="config-grid">
                    <ToggleChoice
                        label="Define type of invoice?"
                        value={form.typeOfInvoiceEnabled}
                        onChange={(value) => updateField("typeOfInvoiceEnabled", value)}
                    />

                    {form.typeOfInvoiceEnabled === "Y" ? (
                        <div className="config-field">
                            <label>Assignment mode</label>
                            <select
                                value={form.typeAssignmentMode}
                                onChange={(event) => updateField("typeAssignmentMode", event.target.value)}
                            >
                                <option value="manual">Manual</option>
                                <option value="automatic">Automatic</option>
                            </select>
                        </div>
                    ) : null}
                </div>

                {form.typeOfInvoiceEnabled === "Y" && form.typeAssignmentMode === "automatic" ? (
                    <div className="config-field">
                        <label>Automatic type logic</label>
                        <select
                            value={form.automaticTypeChoice}
                            onChange={(event) => updateField("automaticTypeChoice", event.target.value)}
                        >
                            <option value="">Select automatic logic</option>
                            <option value="po_grn_mapping">Map against Open PO / GRN report</option>
                            <option value="vendor_master">Use vendor master type definition</option>
                        </select>
                    </div>
                ) : null}
            </Section>

        </section>
    );
}
